import { useState } from 'react';
import PropTypes from 'prop-types';
// material
import { experimentalStyled as styled } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Alert,
  AlertTitle,
  Box,
  Collapse,
  IconButton,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import SupportIcon from '@mui/icons-material/Support';
import { useConnectChat } from 'src/hooks/useConnectChat';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(8, 2),
  backgroundColor: theme.palette.background.default,
}));

const ExpandMore = styled((props) => {
  const { expanded, ...other } = props;
  return <IconButton {...other} size="large" />;
})(({ theme, expanded }) => ({
  transform: !expanded ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  color: '#FF4842',
}));

const linkStyle = {
  color: '#00AB55',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontWeight: 500,
};

const useStyles = makeStyles((theme) => ({
  action: {
    paddingLeft: 0,
  },
  errorDetails: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    backgroundColor: theme.palette.background.paper,
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    maxHeight: '300px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));

// ----------------------------------------------------------------------

ErrorContent.propTypes = {
  title: PropTypes.string.isRequired,
  error: PropTypes.node,
};

export default function ErrorContent({ title, error, ...other }) {
  const classes = useStyles();

  const [_expanded, setExpanded] = useState(false);

  const { openChat: openSupportChat, isLoaded: supportLoaded } =
    useConnectChat();

  const handleReload = () => {
    window.location.reload();
  };

  const getErrorType = () => {
    if (!error) {
      return 'Unknown Error';
    }

    const errorString = error.toString();
    if (errorString.includes('TypeError')) {
      return 'Type Error';
    }
    if (errorString.includes('ReferenceError')) {
      return 'Reference Error';
    }
    if (errorString.includes('SyntaxError')) {
      return 'Syntax Error';
    }
    if (errorString.includes('NetworkError')) {
      return 'Network Error';
    }
    return 'Runtime Error';
  };

  return (
    <RootStyle {...other}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Error Icon and Title */}
          <Box sx={{ textAlign: 'center' }}>
            <BugReportIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
              {title}
            </Typography>
            <Chip
              label={getErrorType()}
              color="error"
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Error Description */}
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', textAlign: 'center' }}
          >
            We encountered an unexpected error. This has been logged and our
            team will investigate.
          </Typography>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleReload}
              sx={{ minWidth: 120 }}
            >
              Reload Page
            </Button>
            <Button
              variant="outlined"
              startIcon={<SupportIcon />}
              onClick={openSupportChat}
              disabled={!supportLoaded}
              sx={{ minWidth: 120 }}
            >
              Get Help
            </Button>
          </Stack>

          {/* Error Details */}
          {error && (
            <Alert
              severity="error"
              sx={{
                width: '100%',
                textAlign: 'left',
                '& .MuiAlert-message': { width: '100%' },
              }}
              classes={{
                action: classes.action,
              }}
              action={
                <ExpandMore
                  size="small"
                  color="error"
                  expanded={_expanded}
                  onClick={() => setExpanded((expanded) => !expanded)}
                  aria-label="Toggle error details"
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              }
            >
              <AlertTitle sx={{ marginBottom: '8px', fontWeight: 600 }}>
                Error Details
              </AlertTitle>
              <Collapse in={_expanded}>
                <Box className={classes.errorDetails}>{error}</Box>
              </Collapse>
            </Alert>
          )}

          {/* Support Information */}
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center' }}
          >
            If this problem persists, please contact&nbsp;
            <span
              style={supportLoaded ? linkStyle : {}}
              onClick={supportLoaded ? openSupportChat : () => null}
            >
              support
            </span>
            &nbsp;with the error details above.
          </Typography>
        </Stack>
      </Paper>
    </RootStyle>
  );
}
